import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { confirmSignUp, resendSignUpCode } from 'aws-amplify/auth';
import { clearError } from '../../store/slices/authSlice';

const validationSchema = Yup.object({
  code: Yup.string()
    .required('Verification code is required')
    .length(6, 'Verification code must be 6 digits')
});

const ConfirmSignup = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get registration data from navigation state
  const registrationData = location.state?.registrationData;
  const email = registrationData?.email || '';
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [resending, setResending] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Redirect to register if no registration data
  React.useEffect(() => {
    if (!registrationData) {
      navigate('/register');
    }
  }, [registrationData, navigate]);

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('[ConfirmSignup] Confirming signup for email:', email);
      
      const { isSignUpComplete } = await confirmSignUp({
        username: email,
        confirmationCode: values.code,
      });
      
      if (isSignUpComplete) {
        console.log('[ConfirmSignup] Signup confirmed successfully');
        setSuccessMessage('Email verified successfully! You can now sign in.');
        
        // Wait 2 seconds then redirect to login
        setTimeout(() => {
          navigate('/login', {
            state: {
              message: 'Registration complete! Please sign in with your credentials.',
              email: email
            }
          });
        }, 2000);
      } else {
        throw new Error('Signup confirmation not complete');
      }
    } catch (error) {
      console.error('[ConfirmSignup] Error confirming signup:', error);
      setError(error.message || 'Failed to verify email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    try {
      setResending(true);
      setError(null);
      setSuccessMessage('');
      
      console.log('[ConfirmSignup] Resending confirmation code for email:', email);
      
      await resendSignUpCode({
        username: email,
      });
      
      setSuccessMessage('Verification code sent! Please check your email.');
    } catch (error) {
      console.error('[ConfirmSignup] Error resending code:', error);
      setError(error.message || 'Failed to resend verification code. Please try again.');
    } finally {
      setResending(false);
    }
  };

  if (!registrationData) {
    return null; // Will redirect
  }

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
          <Typography component="h1" variant="h4" align="center" gutterBottom>
            Verify Your Email
          </Typography>
          <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 3 }}>
            We sent a verification code to <strong>{email}</strong>.<br />
            Please enter the 6-digit code below to complete your registration.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {successMessage && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {successMessage}
            </Alert>
          )}

          <Formik
            initialValues={{ code: '' }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ errors, touched, values, handleChange, handleBlur }) => (
              <Form>
                <Field
                  as={TextField}
                  name="code"
                  label="Verification Code"
                  fullWidth
                  margin="normal"
                  value={values.code}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.code && errors.code}
                  helperText={touched.code && errors.code}
                  inputProps={{ 
                    maxLength: 6,
                    style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5em' }
                  }}
                  placeholder="000000"
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2 }}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Verify Email'}
                </Button>

                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <Button
                    variant="text"
                    onClick={handleResendCode}
                    disabled={resending}
                    sx={{ textTransform: 'none' }}
                  >
                    {resending ? (
                      <>
                        <CircularProgress size={16} sx={{ mr: 1 }} />
                        Sending...
                      </>
                    ) : (
                      'Didn\'t receive the code? Resend'
                    )}
                  </Button>
                </Box>

                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2">
                    Want to use a different email?{' '}
                    <Link to="/register" style={{ color: '#2c5530' }}>
                      Register again
                    </Link>
                  </Typography>
                </Box>
              </Form>
            )}
          </Formik>
        </Paper>
      </Box>
    </Container>
  );
};

export default ConfirmSignup;